import {
  addToDate,
  isDateInFuture,
  useLocalStorage,
  useNetworkStatus,
} from '@airship-platform/airship-patterns';
import {
  ProductsContext,
  ProductsProvider,
  useProducts,
  type ProductsState,
} from '@airship-platform/airship-proposal';
import { PropsWithChildren, useCallback, useState } from 'react';
import { useOfflineOptIn } from '../../rxdb/useOfflineOptIn';
import { useOfflineProviderInit } from '../../rxdb/useOfflineProviderInit';
import { useOfflineDbData } from '../../rxdb/utils/OfflineDbProvider';
import RxDBSetupNotificationAlert from '../../rxdb/utils/RxDBSetupNotificationAlert';
import {
  fetchPricebookSyncFile,
  parsePricebookSyncHashes,
  syncLookups,
  syncModifiers,
  syncPricebook,
} from './pricebook.replication';
import {
  reportPricebookSyncFailure,
  trackPricebookSyncDownload,
} from './pricebook.telemetry';
import { queryPricebookOffline } from './queryPricebookOffline';

const OFFLINE_SYNC_PRICEBOOK_KEY = 'offline-sync-pricebook';
const OFFLINE_SYNC_PRICEBOOK_HASHES_KEY = 'offline-sync-pricebook-hashes';
const OFFLINE_SYNC_PRICEBOOK_ATTEMPT_KEY = 'offline-sync-pricebook-attempt';
const OFFLINE_SYNC_LOOKUPS_KEY = 'offline-sync-lookups';
const OFFLINE_SYNC_MODIFIERS_KEY = 'offline-sync-modifiers';
const OFFLINE_SYNC_HOURS = 1;
const syncedInLastNHours = (offlineSync: string, hours = OFFLINE_SYNC_HOURS) =>
  isDateInFuture(addToDate(hours, 'h', offlineSync));
const setSyncedNow = (setFn: (value: string) => void) =>
  setFn(addToDate(0, 'h').toISOString());

const OfflinePricebookOverride: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const context = useProducts();
  const data = useOfflineDbData();
  const {
    fetchProducts: fetchProductsContext,
    fetchProduct: fetchProductContext,
    fetchLookups,
    fetchModifiers,
    fetchPricebookSyncManifest,
    setProducts,
    setLookups,
  } = context;
  const [pricebookLastSyncTime, setPricebookLastSyncTime] = useLocalStorage(
    OFFLINE_SYNC_PRICEBOOK_KEY
  );
  const [pricebookSyncedHashes, setPricebookSyncedHashes] = useLocalStorage(
    OFFLINE_SYNC_PRICEBOOK_HASHES_KEY
  );
  const [pricebookLastAttemptTime, setPricebookLastAttemptTime] =
    useLocalStorage(OFFLINE_SYNC_PRICEBOOK_ATTEMPT_KEY);
  const [pricebookSyncError, setPricebookSyncError] = useState<string | null>(
    null
  );
  const [lookupsLastSyncTime, setLookupsLastSyncTime] = useLocalStorage(
    OFFLINE_SYNC_LOOKUPS_KEY
  );
  const [lookupsSyncError, setLookupsSyncError] = useState<string | null>(null);
  const [modifiersLastSyncTime, setModifiersLastSyncTime] = useLocalStorage(
    OFFLINE_SYNC_MODIFIERS_KEY
  );
  const [modifiersSyncError, setModifiersSyncError] = useState<string | null>(
    null
  );
  const isOnline = useNetworkStatus();
  const { isOptedIn } = useOfflineOptIn();

  const handleSyncError = useCallback(
    (setError: (value: string | null) => void) => (error: unknown) =>
      setError(`${error}`),
    []
  );

  const populateLookupsState = useCallback(
    () =>
      setLookups(() =>
        Promise.all([
          data.find('pricebook_categories'),
          data.find('pricebook_brands'),
          data.find('pricebook_discounts'),
          data.find('pricebook_fees'),
          data.find('pricebook_rebates'),
        ]).then(([categories, brands, discounts, fees, rebates]) => ({
          categories,
          brands,
          discounts,
          fees,
          rebates,
        }))
      ).catch(handleSyncError(setLookupsSyncError)),
    [data, setLookups, handleSyncError]
  );

  const syncOfflinePricebookItems = useCallback(
    async (options: { force?: boolean }) => {
      setPricebookSyncError(null);

      if (isOnline()) {
        await syncPricebook(
          data,
          fetchPricebookSyncManifest,
          fetchPricebookSyncFile,
          {
            force: options.force,
            syncedHashes: parsePricebookSyncHashes(pricebookSyncedHashes),
          }
        )
          .then(({ hashes, downloads }) => {
            setPricebookSyncedHashes(JSON.stringify(hashes));
            setSyncedNow(setPricebookLastSyncTime);
            downloads.forEach(trackPricebookSyncDownload);
          })
          .catch((error: unknown) => {
            setSyncedNow(setPricebookLastAttemptTime);
            reportPricebookSyncFailure(error);
            handleSyncError(setPricebookSyncError)(error);
          });
      }
    },
    [
      data,
      fetchPricebookSyncManifest,
      handleSyncError,
      isOnline,
      pricebookSyncedHashes,
      setPricebookLastAttemptTime,
      setPricebookLastSyncTime,
      setPricebookSyncedHashes,
    ]
  );

  const syncOfflineLookups = useCallback(async () => {
    setLookupsSyncError(null);
    if (isOnline()) {
      await syncLookups(data, fetchLookups)
        .then(() => setSyncedNow(setLookupsLastSyncTime))
        .catch(handleSyncError(setLookupsSyncError));
    }
  }, [data, fetchLookups, handleSyncError, isOnline, setLookupsLastSyncTime]);

  const syncOfflineModifiers = useCallback(async () => {
    setModifiersSyncError(null);
    if (isOnline()) {
      await syncModifiers(data, fetchModifiers)
        .then(() => setSyncedNow(setModifiersLastSyncTime))
        .catch(handleSyncError(setModifiersSyncError));
    }
  }, [
    fetchModifiers,
    data,
    handleSyncError,
    isOnline,
    setModifiersLastSyncTime,
  ]);

  const syncOfflinePricebook = useCallback(
    (options: { force?: boolean }) =>
      Promise.all([
        syncOfflinePricebookItems(options),
        syncOfflineLookups(),
        syncOfflineModifiers(),
      ]).then(populateLookupsState),
    [
      syncOfflineLookups,
      syncOfflinePricebookItems,
      syncOfflineModifiers,
      populateLookupsState,
    ]
  );

  const init = useCallback(async () => {
    const syncedRecently =
      !!pricebookLastSyncTime && syncedInLastNHours(pricebookLastSyncTime);
    const attemptedRecently =
      !!pricebookLastAttemptTime && syncedInLastNHours(pricebookLastAttemptTime);

    if (!syncedRecently && !attemptedRecently) {
      syncOfflinePricebookItems({});
    }

    if (!lookupsLastSyncTime || !syncedInLastNHours(lookupsLastSyncTime)) {
      syncOfflineLookups().then(populateLookupsState);
    } else {
      syncOfflineModifiers().then(populateLookupsState);
    }
  }, [
    lookupsLastSyncTime,
    populateLookupsState,
    pricebookLastAttemptTime,
    pricebookLastSyncTime,
    syncOfflineLookups,
    syncOfflinePricebookItems,
    syncOfflineModifiers,
  ]);

  const { initError } = useOfflineProviderInit(init);

  const fetchProducts: typeof context.fetchProducts = useCallback(
    (filterOptions, options) => {
      const offlineFetch = async () => {
        await setProducts(filterOptions, async () => {
          if (options?.signal?.aborted) {
            return [];
          }

          if (filterOptions.pricebookIds?.length) {
            return data.find('pricebook_items', {
              selector: {
                pricebookId: { $in: filterOptions.pricebookIds },
              },
            });
          }

          const typeSelector = filterOptions.pricebookTypes?.length
            ? { selector: { type: { $in: filterOptions.pricebookTypes } } }
            : {};

          const [products, cats] = await Promise.all([
            data.find('pricebook_items', typeSelector),
            data.find('pricebook_categories', {}),
          ]);
          const filtered = queryPricebookOffline(products, filterOptions, cats);
          if (options?.signal?.aborted) {
            return [];
          }

          return filtered;
        });

        return [];
      };

      if (isOptedIn) {
        return offlineFetch();
      }

      return fetchProductsContext(filterOptions, options);
    },
    [data, fetchProductsContext, isOptedIn, setProducts]
  );

  const fetchProduct: typeof context.fetchProduct = useCallback(
    (req, options) => {
      if (isOptedIn) {
        return setProducts({}, async () => {
          if (options?.signal?.aborted) {
            return [];
          }

          const productDoc = await data.findOne(
            'pricebook_items',
            req.pricebookId
          );

          return productDoc ? [productDoc] : [];
        });
      } else {
        return fetchProductContext(req, options);
      }
    },
    [data, fetchProductContext, isOptedIn, setProducts]
  );

  if (initError) {
    return (
      <RxDBSetupNotificationAlert
        error={initError}
        entityName="pricebook products"
      />
    );
  }

  return (
    <ProductsContext.Provider
      value={{
        ...context,
        fetchProduct,
        fetchProducts,
        syncOfflinePricebook,
        pricebookSync: {
          error: pricebookSyncError,
          lastSyncTime: pricebookLastSyncTime,
        },
        lookupsSync: {
          error: lookupsSyncError,
          lastSyncTime: lookupsLastSyncTime,
        },
        modifiersSync: {
          error: modifiersSyncError,
          lastSyncTime: modifiersLastSyncTime,
        },
      }}
    >
      {children}
    </ProductsContext.Provider>
  );
};

type OwnProps = PropsWithChildren<{
  initialState?: Partial<ProductsState>;
}>;

export const OfflinePricebookProvider: React.FC<OwnProps> = ({
  children,
  initialState,
}) => (
  <ProductsProvider initialState={initialState}>
    <OfflinePricebookOverride>{children}</OfflinePricebookOverride>
  </ProductsProvider>
);
